import { Button, Card, OrderButtons, SectionLabel } from '../../design/components'
import type { InitiativeEntry } from './types'
import styles from './live.module.css'

export interface InitiativeRailProps {
  entries: readonly InitiativeEntry[]
  round: number
  currentIndex: number
  canControl: boolean
  onReorder: (from: number, to: number) => void
  onAdvance: () => void
  busy: boolean
  /**
   * Drop the card wrapper and tighten the rows.
   *
   * The Game Master's column already has a card around this and no room to spare; a player's
   * screen has room and needs the heading. Same list, two densities.
   */
  compact?: boolean
}

/**
 * The order, whose turn it is, and the round.
 *
 * Reordering is `OrderButtons` — the same control the prompt composer uses — rather than a drag.
 * A drag is offered on the map because a map is a spatial thing and dragging is what it is for;
 * an ordered list is not, and buttons here are operable from a keyboard, announced properly, and
 * work on a phone without a long-press gesture nobody discovers.
 */
export function InitiativeRail({
  entries,
  round,
  currentIndex,
  canControl,
  onReorder,
  onAdvance,
  busy,
  compact = false,
}: InitiativeRailProps) {
  const body = (
    <>
      {entries.length === 0 ? (
        <p className={styles.note}>
          {canControl
            ? 'Nobody in the order yet. Starting the encounter puts everyone the table has met into it.'
            : 'The Game Master has not set an order yet.'}
        </p>
      ) : (
        <ol
          className={`${styles.initiative} ${compact ? styles.initiativeCompact : ''}`}
          aria-label="Initiative order"
        >
          {entries.map((entry, index) => (
            <li
              key={entry.entryId}
              className={`${styles.initiativeRow} ${index === currentIndex ? styles.initiativeCurrent : ''}`}
              // Announced rather than only coloured: whose turn it is is the single most
              // important fact on this card, and a border says nothing to a screen reader.
              aria-current={index === currentIndex ? 'step' : undefined}
            >
              <span className={styles.initiativePosition} aria-hidden="true">
                {index + 1}
              </span>

              <span className={styles.initiativeName}>
                {entry.label}
                {entry.hasActed && index !== currentIndex && (
                  <span className={styles.rowMeta}> · acted</span>
                )}
              </span>

              {entry.value !== null && (
                <span className={styles.initiativeValue}>{entry.value}</span>
              )}

              {canControl && (
                <OrderButtons
                  label={entry.label}
                  index={index}
                  total={entries.length}
                  onMove={(to) => onReorder(index, to)}
                />
              )}
            </li>
          ))}
        </ol>
      )}

      {/* Compact means the Game Master's column, where passing the turn lives beside the
          initiative strip instead — one control for one action, not two in two places. */}
      {canControl && entries.length > 0 && !compact && (
        <>
          <SectionLabel tone="muted" micro>
            The turn
          </SectionLabel>
          <Button fullWidth disabled={busy} onClick={onAdvance}>
            {currentIndex < 0 ? 'Start the round' : 'Next in order'}
          </Button>
        </>
      )}
    </>
  )

  // The Game Master's column supplies its own card; a player's screen does not.
  return compact ? (
    body
  ) : (
    <Card variant="powers" label="Initiative" hint={`Round ${round}`}>
      {body}
    </Card>
  )
}
